'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  loadOperationsRuntimeSnapshot,
  OPERATIONS_RUNTIME_SNAPSHOT_UPDATED_EVENT,
  type OperationsRuntimeSnapshot,
  type OperationsRuntimeSnapshotUpdatedDetail,
} from './operationsRuntimeSnapshot';

interface UseOperationsRuntimeSnapshotOptions {
  clientId?: number | null;
  userId?: number | null;
  sourceId?: string;
  enabled?: boolean;
}

function hasValidIdentity(clientId?: number | null, userId?: number | null): boolean {
  return Number.isFinite(clientId) && Number(clientId) > 0 && Number.isFinite(userId) && Number(userId) > 0;
}

export function useOperationsRuntimeSnapshot(options: UseOperationsRuntimeSnapshotOptions) {
  const {
    clientId,
    userId,
    sourceId,
    enabled = true,
  } = options;

  const [snapshot, setSnapshot] = useState<OperationsRuntimeSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !hasValidIdentity(clientId, userId)) {
      setSnapshot(null);
      setIsLoading(false);
      return null;
    }

    setIsLoading(true);
    try {
      const nextSnapshot = await loadOperationsRuntimeSnapshot(Number(clientId), Number(userId));
      setSnapshot(nextSnapshot);
      return nextSnapshot;
    } finally {
      setIsLoading(false);
    }
  }, [clientId, enabled, userId]);

  useEffect(() => {
    let cancelled = false;

    if (!enabled || !hasValidIdentity(clientId, userId)) {
      setSnapshot(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);
    void loadOperationsRuntimeSnapshot(Number(clientId), Number(userId))
      .then((nextSnapshot) => {
        if (!cancelled) {
          setSnapshot(nextSnapshot);
        }
      })
      .catch((error) => {
        console.warn('[useOperationsRuntimeSnapshot] Failed to hydrate runtime snapshot:', error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, enabled, userId]);

  useEffect(() => {
    if (typeof window === 'undefined' || !enabled || !hasValidIdentity(clientId, userId)) {
      return;
    }

    const handleSnapshotUpdated = (event: Event) => {
      const detail = (event as CustomEvent<OperationsRuntimeSnapshotUpdatedDetail>).detail;
      if (!detail || detail.clientId !== Number(clientId) || detail.userId !== Number(userId)) {
        return;
      }

      if (sourceId && detail.source === sourceId) {
        return;
      }

      setSnapshot(detail.snapshot);
    };

    window.addEventListener(OPERATIONS_RUNTIME_SNAPSHOT_UPDATED_EVENT, handleSnapshotUpdated as EventListener);
    return () => {
      window.removeEventListener(OPERATIONS_RUNTIME_SNAPSHOT_UPDATED_EVENT, handleSnapshotUpdated as EventListener);
    };
  }, [clientId, enabled, sourceId, userId]);

  return {
    snapshot,
    isLoading,
    refresh,
  };
}

export default useOperationsRuntimeSnapshot;